import {
  keepPreviousData,
  useInfiniteQuery,
  useQuery,
  useQueryClient,
  type UseQueryResult,
} from '@tanstack/react-query';
import type { CreateTicketBody, SupportMailboxFilter, TicketStatus } from '@brimo/shared';
import { ApiError, api } from '../../lib/api';
import { queryRoots } from '../../lib/events';
import { uploadWithProgress } from '../properties/upload';
import type {
  AfterSalesBoardData,
  AfterSalesRecord,
  BoardFilters,
  ContactHit,
  DeveloperOption,
  MailboxList,
  PropertyOption,
  StatusAfter,
  TicketBoard,
  TicketDetail,
  UnitOption,
} from './types';

/**
 * Queries and mutations of Tools › Tickets (docs/dev/api-tickets.md). Keys live under the realtime roots
 * `['tickets']` (board), `['ticket', id]` (record), `['support-mailbox']`, `['after-sales']` (board) and
 * `['after-sales-case', id]` so that `ticket.updated`, `inbox.updated` and `after_sales.updated` refresh them.
 */
export const ticketKeys = {
  board: (filters: BoardFilters) => [...queryRoots.tickets, 'board', filters] as const,
  detail: (id: string) => [...queryRoots.ticket, id] as const,
  mailbox: (filter: SupportMailboxFilter, q: string) => [...queryRoots.supportMailbox, filter, q] as const,
  afterSalesBoard: (propertyId: string | null) =>
    [...queryRoots.afterSales, 'board', propertyId ?? ''] as const,
  afterSalesCase: (id: string) => [...queryRoots.afterSalesCase, id] as const,
};

function query(params: Record<string, string | null | undefined>): string {
  const search = new URLSearchParams();
  for (const [key, value] of Object.entries(params)) if (value) search.set(key, value);
  const text = search.toString();
  return text ? `?${text}` : '';
}

function noRetryOnAccess(count: number, error: Error): boolean {
  if (error instanceof ApiError && (error.status === 403 || error.status === 404)) return false;
  return count < 1;
}

// --- tickets ---------------------------------------------------------------------------------------------

export function useTicketBoard(filters: BoardFilters): UseQueryResult<TicketBoard, Error> {
  return useQuery({
    queryKey: ticketKeys.board(filters),
    queryFn: ({ signal }) =>
      api<TicketBoard>(
        `/tickets/board${query({
          filter: filters.filter === 'all' ? null : filters.filter,
          categoryId: filters.categoryId,
          assigneeId: filters.assigneeId,
          q: filters.q || null,
        })}`,
        { signal },
      ),
    placeholderData: keepPreviousData,
    retry: noRetryOnAccess,
    staleTime: 15_000,
  });
}

export function useTicket(id: string): UseQueryResult<TicketDetail, Error> {
  return useQuery({
    queryKey: ticketKeys.detail(id),
    queryFn: ({ signal }) => api<TicketDetail>(`/tickets/${id}`, { signal }),
    retry: noRetryOnAccess,
    staleTime: 10_000,
  });
}

export function createTicket(body: CreateTicketBody): Promise<TicketDetail> {
  return api<TicketDetail>('/tickets', { method: 'POST', body });
}

export function changeTicketStatus(id: string, to: TicketStatus): Promise<TicketDetail> {
  return api<TicketDetail>(`/tickets/${id}/status`, { method: 'POST', body: { to } });
}

export function takeTicket(id: string): Promise<TicketDetail> {
  return api<TicketDetail>(`/tickets/${id}/take`, { method: 'POST' });
}

export function assignTicket(id: string, assigneeId: string): Promise<TicketDetail> {
  return api<TicketDetail>(`/tickets/${id}/assign`, { method: 'POST', body: { assigneeId } });
}

export function patchTicket(id: string, body: { categoryId: string | null }): Promise<TicketDetail> {
  return api<TicketDetail>(`/tickets/${id}`, { method: 'PATCH', body });
}

export function remindTicket(id: string): Promise<TicketDetail> {
  return api<TicketDetail>(`/tickets/${id}/remind`, { method: 'POST' });
}

export interface TicketMessageInput {
  kind: 'reply' | 'note';
  text: string;
  statusAfter: StatusAfter | null;
  files: File[];
}

/** `POST /tickets/:id/messages` (multipart) with upload progress 0…1. */
export function sendTicketMessage(
  id: string,
  input: TicketMessageInput,
  onProgress: (fraction: number) => void,
): Promise<TicketDetail> {
  const form = new FormData();
  form.append('kind', input.kind);
  form.append('text', input.text);
  if (input.kind === 'reply' && input.statusAfter) form.append('statusAfter', input.statusAfter);
  for (const file of input.files) form.append('files', file, file.name);
  return uploadWithProgress<TicketDetail>(`/tickets/${id}/messages`, form, { onProgress });
}

// --- support mailbox -------------------------------------------------------------------------------------

export function useSupportMailbox(filter: SupportMailboxFilter, q: string) {
  return useInfiniteQuery({
    queryKey: ticketKeys.mailbox(filter, q),
    queryFn: ({ pageParam, signal }) =>
      api<MailboxList>(
        `/support-mailbox${query({
          filter: filter === 'unassigned' ? null : filter,
          q: q || null,
          cursor: pageParam,
        })}`,
        { signal },
      ),
    initialPageParam: null as string | null,
    getNextPageParam: (last) => last.nextCursor,
    placeholderData: keepPreviousData,
    retry: noRetryOnAccess,
    staleTime: 15_000,
  });
}

export type MailAssignTarget = { contactId: string } | { dealId: string } | { caseId: string };

export function assignMail(id: string, target: MailAssignTarget): Promise<MailboxList> {
  return api<MailboxList>(`/support-mailbox/${id}/assign`, { method: 'POST', body: target });
}

export function ticketFromMail(id: string): Promise<{ ticketId: string; numberLabel: string }> {
  return api<{ ticketId: string; numberLabel: string }>(`/support-mailbox/${id}/ticket`, {
    method: 'POST',
    body: {},
  });
}

export function ignoreMail(id: string): Promise<MailboxList> {
  return api<MailboxList>(`/support-mailbox/${id}/ignore`, { method: 'POST' });
}

export function notifyMailboxAdmins(): Promise<{ notified: number }> {
  return api<{ notified: number }>('/support-mailbox/notify-admins', { method: 'POST' });
}

/** „Neu verbinden" (api-connections.md): the hosted Unipile link the browser opens next. */
export function reconnectMailbox(connectionId: string): Promise<{ connectionId: string; url: string }> {
  return api<{ connectionId: string; url: string }>(`/connections/${connectionId}/reconnect`, {
    method: 'POST',
    body: {},
  });
}

// --- after-sales ----------------------------------------------------------------------------------------

export function useAfterSalesBoard(propertyId: string | null): UseQueryResult<AfterSalesBoardData, Error> {
  return useQuery({
    queryKey: ticketKeys.afterSalesBoard(propertyId),
    queryFn: ({ signal }) =>
      api<AfterSalesBoardData>(`/after-sales/board${query({ propertyId })}`, { signal }),
    placeholderData: keepPreviousData,
    retry: noRetryOnAccess,
    staleTime: 15_000,
  });
}

export function useAfterSalesCase(id: string): UseQueryResult<AfterSalesRecord, Error> {
  return useQuery({
    queryKey: ticketKeys.afterSalesCase(id),
    queryFn: ({ signal }) => api<AfterSalesRecord>(`/after-sales/${id}`, { signal }),
    retry: noRetryOnAccess,
    staleTime: 10_000,
  });
}

export interface CreateCaseInput {
  unitId: string;
  title: string;
  description: string;
  ticketId: string | null;
  photos: File[];
}

/** `POST /after-sales` (multipart, ≤ 5 photos) with upload progress 0…1. */
export function createAfterSalesCase(
  input: CreateCaseInput,
  onProgress: (fraction: number) => void,
): Promise<AfterSalesRecord> {
  const form = new FormData();
  form.append('unitId', input.unitId);
  form.append('title', input.title);
  if (input.description.trim()) form.append('description', input.description.trim());
  if (input.ticketId) form.append('ticketId', input.ticketId);
  for (const file of input.photos) form.append('photos', file, file.name);
  return uploadWithProgress<AfterSalesRecord>('/after-sales', form, { onProgress });
}

export interface AssignExternalInput {
  developerId?: string;
  company?: string;
  purpose?: string;
  contactPerson?: string;
  phone?: string;
  deadlineOn?: string;
}

export function assignExternal(id: string, body: AssignExternalInput): Promise<AfterSalesRecord> {
  return api<AfterSalesRecord>(`/after-sales/${id}/assign-external`, { method: 'POST', body });
}

export function scheduleCase(id: string, appointmentAt: string): Promise<AfterSalesRecord> {
  return api<AfterSalesRecord>(`/after-sales/${id}/schedule`, { method: 'POST', body: { appointmentAt } });
}

export function completeCase(id: string): Promise<AfterSalesRecord> {
  return api<AfterSalesRecord>(`/after-sales/${id}/complete`, { method: 'POST' });
}

export function extendCase(id: string, deadlineOn: string): Promise<AfterSalesRecord> {
  return api<AfterSalesRecord>(`/after-sales/${id}/extend`, { method: 'POST', body: { deadlineOn } });
}

export function remindCase(id: string): Promise<AfterSalesRecord> {
  return api<AfterSalesRecord>(`/after-sales/${id}/remind`, { method: 'POST' });
}

export function logCase(id: string, text: string): Promise<AfterSalesRecord> {
  return api<AfterSalesRecord>(`/after-sales/${id}/log`, { method: 'POST', body: { text } });
}

export function patchCase(
  id: string,
  body: { customerStatusText: string | null },
): Promise<AfterSalesRecord> {
  return api<AfterSalesRecord>(`/after-sales/${id}`, { method: 'PATCH', body });
}

// --- pickers ---------------------------------------------------------------------------------------------

export function useContactSearch(q: string) {
  const trimmed = q.trim();
  return useQuery({
    queryKey: [...queryRoots.contacts, 'ticket-picker', trimmed],
    queryFn: ({ signal }) =>
      api<{ items: ContactHit[] }>(`/contacts${query({ q: trimmed, limit: '8' })}`, { signal }),
    enabled: trimmed.length >= 2,
    placeholderData: keepPreviousData,
    staleTime: 30_000,
  });
}

export function usePropertyOptions(enabled: boolean) {
  return useQuery({
    queryKey: [...queryRoots.properties, 'after-sales-picker'],
    queryFn: ({ signal }) => api<{ items: PropertyOption[] }>('/properties?limit=100', { signal }),
    enabled,
    staleTime: 60_000,
  });
}

export function useUnitOptions(propertyId: string | null) {
  return useQuery({
    queryKey: [...queryRoots.units, 'after-sales-picker', propertyId ?? ''],
    queryFn: ({ signal }) =>
      api<{ items: UnitOption[] }>(`/properties/${propertyId ?? ''}/units?status=all&limit=200`, { signal }),
    enabled: propertyId !== null,
    staleTime: 60_000,
  });
}

export function useDeveloperOptions(enabled: boolean) {
  return useQuery({
    queryKey: [...queryRoots.properties, 'developers-picker'],
    queryFn: ({ signal }) => api<{ items: DeveloperOption[] }>('/developers?limit=100', { signal }),
    enabled,
    staleTime: 60_000,
  });
}

/** Refreshes the ticket module after an own mutation (the realtime events do the same for other tabs). */
export function useInvalidateTickets() {
  const queryClient = useQueryClient();
  return async () => {
    await Promise.all([
      queryClient.invalidateQueries({ queryKey: [...queryRoots.tickets] }),
      queryClient.invalidateQueries({ queryKey: [...queryRoots.ticket] }),
      queryClient.invalidateQueries({ queryKey: [...queryRoots.supportMailbox] }),
      queryClient.invalidateQueries({ queryKey: [...queryRoots.afterSales] }),
      queryClient.invalidateQueries({ queryKey: [...queryRoots.afterSalesCase] }),
      queryClient.invalidateQueries({ queryKey: [...queryRoots.contact] }),
    ]);
  };
}
